import { useEffect, useMemo, useState } from "react";
import { api, formatError, formatNZD, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { REGION_NAMES, SITE_REGION } from "@/lib/regions";
import { Megaphone, Globe2, ImageUp, Eye, MousePointerClick, RotateCw } from "lucide-react";

const quote = (p, f) => {
  if (!p) return 0;
  const band = p[f.period];
  if (f.everywhere) return band.everywhere;
  return f.regions.length ? band.region + band.extra_region * (f.regions.length - 1) : 0;
};

function PriceList({ p }) {
  return (
    <div className="grid grid-cols-2 gap-3 text-sm" data-testid="banner-price-list">
      {["week", "month"].map((k) => (
        <div key={k} className="rounded-xl border border-emerald-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-widest font-black text-emerald-700">Per {k}</div>
          <div>1 region <b>{formatNZD(p[k].region)}</b></div>
          <div>Each extra region <b>+{formatNZD(p[k].extra_region)}</b></div>
          <div className="text-emerald-800">Everywhere (national) <b>{formatNZD(p[k].everywhere)}</b></div>
        </div>
      ))}
    </div>
  );
}

function RegionPicker({ f, setF }) {
  const toggle = (r) => setF({ ...f, regions: f.regions.includes(r) ? f.regions.filter((x) => x !== r) : [...f.regions, r] });
  return (
    <div className="space-y-2">
      <button type="button" onClick={() => setF({ ...f, everywhere: !f.everywhere })} className={`w-full flex items-center gap-2 rounded-xl border-2 p-3 text-left ${f.everywhere ? "border-emerald-700 bg-emerald-50" : "border-slate-200"}`} data-testid="banner-everywhere">
        <Globe2 className="w-5 h-5 text-emerald-800" /><div><div className="font-bold">Everywhere — national</div><div className="text-xs text-slate-500">The only banners on the markdowns.nz home page, and they also rotate on all 16 regional sites</div></div>
      </button>
      {!f.everywhere && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {REGION_NAMES.map((r) => (
              <button type="button" key={r} onClick={() => toggle(r)} className={`text-xs font-semibold rounded-lg border px-2 py-2 ${f.regions.includes(r) ? "bg-emerald-800 text-white border-emerald-800" : "bg-white border-slate-200 hover:border-emerald-300"}`} data-testid={`banner-region-${r}`}>{r}</button>
            ))}
          </div>
          <p className="text-xs text-slate-500">Regional banners show on the chosen regional sites only. First region you pick is your home region.</p>
        </>
      )}
    </div>
  );
}

export default function Advertise() {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ business_name: "", contact_email: "", contact_phone: "", link_url: "", message: "", image_path: "", period: "week", regions: SITE_REGION ? [SITE_REGION.name] : [], include_hq: false, everywhere: false });
  useEffect(() => { api.get("/banners/pricing").then((r) => setP(r.data)).catch(() => {}); }, []);
  const total = useMemo(() => quote(p, f), [p, f]);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/banners/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setF((x) => ({ ...x, image_path: data.path })); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.image_path) { toast.error("Please upload your banner image"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/banners/book", { ...f, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); setBusy(false); }
  };
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5 space-y-5">
        <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1.5 rounded-full"><Megaphone className="w-3.5 h-3.5" />Banner advertising</div>
        <h1 className="text-4xl sm:text-5xl font-display font-extrabold leading-tight" data-testid="advertise-title">Put your business in front of <span className="text-emerald-800">local shoppers</span>.</h1>
        <p className="text-slate-600">Your banner shows on the front page of the regions you choose. Up to {p?.max_rotation ?? 5} banners share each spot and take turns, so you get real screen time. Book weekly or monthly — it ends unless you renew.</p>
        <ul className="text-sm text-slate-700 space-y-1.5">
          <li className="flex gap-2"><Eye className="w-4 h-4 text-emerald-700 mt-0.5" />Private stats link: views and clicks per region</li>
          <li className="flex gap-2"><MousePointerClick className="w-4 h-4 text-emerald-700 mt-0.5" />Click-through to your website or Facebook page</li>
          <li className="flex gap-2"><RotateCw className="w-4 h-4 text-emerald-700 mt-0.5" />Checked before it goes live — full refund if not approved</li>
        </ul>
        {p && <PriceList p={p} />}
      </div>
      <Card className="lg:col-span-7 border-emerald-200 shadow-lg">
        <CardContent className="pt-6">
          <form onSubmit={submit} className="space-y-4" data-testid="banner-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label>Business name</Label><Input required maxLength={80} value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} data-testid="banner-business" /></div>
              <div><Label>Email (for receipt + stats)</Label><Input required type="email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} data-testid="banner-email" /></div>
              <div><Label>Phone (optional)</Label><Input value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} data-testid="banner-phone" /></div>
              <div><Label>Website link (optional)</Label><Input type="url" placeholder="https://" value={f.link_url} onChange={(e) => setF({ ...f, link_url: e.target.value })} data-testid="banner-link-input" /></div>
            </div>
            <div><Label>Short message on the banner (optional)</Label><Input maxLength={90} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="20% off all paint this week!" data-testid="banner-message-input" /></div>
            <div>
              <Label>Banner image — wide, about 1200 × 300 px</Label>
              <label className="mt-1 flex items-center justify-center gap-2 h-28 rounded-xl border-2 border-dashed border-slate-300 cursor-pointer overflow-hidden hover:border-emerald-400" data-testid="banner-image-drop">
                {f.image_path ? <img src={imageUrl(f.image_path)} alt="Banner preview" className="w-full h-full object-cover" /> : <span className="text-sm text-slate-500 flex items-center gap-2"><ImageUp className="w-4 h-4" />Upload JPG or PNG (max 5MB)</span>}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid="banner-image-input" />
              </label>
            </div>
            <div className="flex gap-2">
              {["week", "month"].map((k) => <Button type="button" key={k} variant={f.period === k ? "default" : "outline"} className={f.period === k ? "bg-emerald-800 hover:bg-emerald-900" : ""} onClick={() => setF({ ...f, period: k })} data-testid={`banner-period-${k}`}>{k === "week" ? "1 week" : "1 month"}</Button>)}
            </div>
            <RegionPicker f={f} setF={setF} />
            <div className="flex items-center justify-between rounded-xl bg-emerald-900 text-white px-4 py-3">
              <div className="text-sm">Total for 1 {f.period}</div>
              <div className="font-display font-black text-2xl" data-testid="banner-total">{formatNZD(total)}</div>
            </div>
            <Button type="submit" disabled={busy || total <= 0} className="w-full h-11 bg-amber-500 hover:bg-amber-600 text-white" data-testid="banner-submit">{busy ? "Opening Stripe…" : "Book & pay securely"}</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
